/**
 * /annotation — two tabs, Langfuse-style. Queues (default): the annotation
 * queues I made or that were shared with me, each a list of experiment results
 * to score by hand. Scores (`?tab=scores`): the score configs every queue picks
 * from. Each has Active and Archived (`?view=archived`). `?new=1` opens "New queue" or "New score", whichever tab is showing.
 */

import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page";
import { Tabs } from "@/components/ui/tabs";
import { useUrlState } from "@/lib/use-url-state";
import { NewQueueDialog } from "./new-queue-dialog";
import { QueuesTab } from "./queues-tab";
import { ScoreConfigDialog } from "./score-config-form";
import { ScoresTab } from "./scores-tab";
import { useProject } from "@/features/projects/hooks";
import { ProjectAction } from "@/features/projects/project-action";

type Tab = "queues" | "scores";

export function AnnotationQueuesPage() {
  const { can } = useProject();
  const url = useUrlState();
  const tab: Tab = url.get("tab") === "scores" ? "scores" : "queues";
  const close = () => url.set({ new: null });
  const newButton = (
    <ProjectAction permission="annotations:write">
      <Button variant="primary" onClick={() => url.set({ new: 1 })}>
        <Plus size={14} strokeWidth={2.2} aria-hidden />
        {tab === "queues" ? "New queue" : "New score"}
      </Button>
    </ProjectAction>
  );

  return (
    <>
      <PageHeader
        title="Annotate"
        subtitle="People score agent outputs by hand. Their scores sit beside the judges' on the experiment page."
        actions={newButton}
      />
      <Tabs
        label="Annotate sections"
        value={tab}
        onChange={(t) => url.set({ tab: t === "queues" ? null : t, new: null, view: null })}
        items={[
          { key: "queues", label: "Queues" },
          { key: "scores", label: "Scores" },
        ]}
      />
      {tab === "queues" ? <QueuesTab newButton={newButton} /> : <ScoresTab newButton={newButton} />}
      {can("annotations:write") &&
        url.get("new") &&
        (tab === "queues" ? <NewQueueDialog onClose={close} /> : <ScoreConfigDialog onClose={close} />)}
    </>
  );
}
